import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import {
  LayoutDashboard,
  CalendarDays,
  Briefcase,
  Users,
  Car,
  ShieldCheck,
  Wallet,
  LogOut,
  Settings,
  Sun,
  Moon,
  Menu,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/calendar", label: "Calendar", icon: CalendarDays },
  { path: "/history", label: "History", icon: Briefcase },
  { path: "/drivers", label: "Drivers", icon: Users },
  { path: "/vehicles", label: "Vehicles", icon: Car },
  { path: "/licensing", label: "Licensing", icon: ShieldCheck },
  { path: "/billing", label: "Billing", icon: Wallet },
];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function CompactClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const day = DAY_NAMES[now.getDay()];
  const date = now.getDate();
  const month = MONTH_NAMES[now.getMonth()];
  const h = String(now.getHours()).padStart(2, "0");
  const m = String(now.getMinutes()).padStart(2, "0");
  return (
    <span className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap" style={{ fontFamily: "'IBM Plex Sans', monospace" }} data-testid="topnav-clock">
      {day} {date} {month} · {h}:{m}
    </span>
  );
}

function ThemeToggle() {
  return (
    <button
      onClick={() => {
        const html = document.documentElement;
        const isLight = html.classList.contains("light");
        html.classList.remove("light", "dark");
        html.classList.add(isLight ? "dark" : "light");
        localStorage.setItem("arrivo_theme", isLight ? "dark" : "light");
      }}
      className="inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
      data-testid="theme-toggle"
    >
      <Sun className="w-4 h-4 hidden dark:block" />
      <Moon className="w-4 h-4 block dark:hidden" />
    </button>
  );
}

export default function TopNav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur-sm" data-testid="topnav">
      <div className="flex items-center h-14 px-4 gap-4">
        {/* Brand */}
        <NavLink to="/" className="flex items-center gap-2 shrink-0" data-testid="topnav-brand">
          <img src="/logo-mark.png" alt="Arrivo" className="w-7 h-7 object-contain" />
          <span
            className="text-lg font-semibold tracking-[0.15em] text-foreground hidden sm:block"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            ARRIVO
          </span>
        </NavLink>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 flex-1" data-testid="topnav-desktop-nav">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                }`}
              >
                <item.icon className="w-4 h-4 shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Right section — desktop */}
        <div className="hidden lg:flex items-center gap-2 ml-auto">
          <CompactClock />
          {user?.role === "admin" && (
            <NavLink
              to="/admin/settings"
              data-testid="admin-settings-link"
              className={({ isActive }) =>
                `inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${
                  isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                }`
              }
            >
              <Settings className="w-4 h-4" />
            </NavLink>
          )}
          <ThemeToggle />
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-muted-foreground hover:text-destructive-foreground hover:bg-destructive/20 h-8 px-2"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>

        {/* Mobile: clock + hamburger */}
        <div className="flex lg:hidden items-center gap-2 ml-auto">
          <CompactClock />
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid="topnav-mobile-menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64 bg-card border-border p-0">
              <div className="flex flex-col h-full">
                {/* Mobile brand */}
                <div className="flex items-center gap-2 px-4 py-4 border-b border-border">
                  <img src="/logo-mark.png" alt="Arrivo" className="w-7 h-7 object-contain" />
                  <span className="text-lg font-semibold tracking-[0.15em] text-foreground" style={{ fontFamily: "'Manrope', sans-serif" }}>ARRIVO</span>
                </div>

                {/* Mobile nav links */}
                <nav className="flex-1 py-3 px-2 space-y-0.5" data-testid="topnav-mobile-nav">
                  {navItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                          isActive
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                        }`}
                      >
                        <item.icon className="w-4.5 h-4.5 shrink-0" style={{ width: 18, height: 18 }} />
                        <span>{item.label}</span>
                      </NavLink>
                    );
                  })}
                </nav>

                {/* Mobile actions */}
                <div className="border-t border-border px-3 py-3 space-y-1">
                  {user?.role === "admin" && (
                    <NavLink
                      to="/admin/settings"
                      onClick={() => setMobileOpen(false)}
                      data-testid="mobile-admin-settings"
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                          isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
                        }`
                      }
                    >
                      <Settings className="w-4 h-4" />
                      <span>Admin Settings</span>
                    </NavLink>
                  )}
                  <div className="flex items-center justify-between px-3 py-2">
                    <span className="text-sm text-muted-foreground">Theme</span>
                    <ThemeToggle />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setMobileOpen(false); logout(); }}
                    className="w-full justify-start gap-3 px-3 py-2 text-muted-foreground hover:text-destructive-foreground hover:bg-destructive/20"
                    data-testid="mobile-logout-button"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
